import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { ProfitAndLossReport } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type ProfitAndLossStatus = "loading" | "ready" | "error";

export interface UseProfitAndLossResult {
  /** Null until the request resolves, or if it failed. */
  report: ProfitAndLossReport | null;
  status: ProfitAndLossStatus;
  error: string | null;
}

/**
 * Fetches the tenant's real Profit &amp; Loss from GET /api/demo/reports/profit-loss.
 *
 * ef-003 made the basis real. Both bases are computed server-side from the SAME rows by
 * different projections (EasyFarm.Domain.RecognitionRules) — neither is stored, so they
 * cannot drift apart. The client asks for one and echoes back whatever the server says it
 * computed, rather than labelling the response itself.
 */
/**
 * @param reloadKey Change it to force a re-read — see useBalanceSheet's remark. A booth
 *   reset wipes the ledger, and Net farm income is computed from it.
 */
export function useProfitAndLoss(
  basis: "cash" | "accrual" = "cash",
  reloadKey: number = 0,
): UseProfitAndLossResult {
  const [report, setReport] = useState<ProfitAndLossReport | null>(null);
  const [status, setStatus] = useState<ProfitAndLossStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getProfitAndLoss(basis, controller.signal);
        if (cancelled) return;
        setReport(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setReport(null);
        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Refetches when the basis changes. ef-003 — accrual is a real report now, computed
    // from the same rows by a different projection, so switching bases is a request rather
    // than a client-side filter.
  }, [basis, reloadKey]);

  return { report, status, error };
}
